import { Icon } from "@/components/ui/Icon";
import { VehicleSelector } from "../VehicleSelector";
import type { Locale } from "@/config/i18n";
import type { Vehicle } from "@/config/infos";
import type { ReservationFormData } from "../types";
import type { ReservationUi } from "../reservationFormUi";

type PassengersSectionProps = {
  t: ReservationUi["step1"];
  locale: Locale;
  formData: ReservationFormData;
  setFormData: React.Dispatch<React.SetStateAction<ReservationFormData>>;
  selectedVehicle: Vehicle;
  showAdvancedPassengerOptions: boolean;
  setShowAdvancedPassengerOptions: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};

/**
 * Section « Véhicule & passagers » : sélecteur de véhicule + compteurs adultes
 * (toujours visibles) et enfants / bébés / bagages (repliables). Bloc
 * présentationnel extrait verbatim de ReservationForm (aucun changement de
 * comportement) — bornes min/max identiques.
 */
export const PassengersSection = ({
  t,
  locale,
  formData,
  setFormData,
  selectedVehicle,
  showAdvancedPassengerOptions,
  setShowAdvancedPassengerOptions,
}: PassengersSectionProps) => {
  return (
    <section className="mb-8">
      <h2 className="mb-6 flex items-center gap-2 text-2xl font-semibold text-black">
        <Icon icon="ph:users" className="h-6 w-6 text-gold-700" />
        <span>{t.vehiclePassengersHeading}</span>
      </h2>

      <VehicleSelector
        selectedVehicleId={formData.selectedVehicleId}
        onSelect={(vehicleId) =>
          setFormData((prev) => ({
            ...prev,
            selectedVehicleId: vehicleId,
          }))
        }
        passengers={formData.passengers}
        luggage={formData.luggage}
        babySeats={formData.babySeats}
        childSeats={formData.childSeats}
        locale={locale}
      />

      <div className="grid gap-6">
        {/* Passagers Adultes */}
        <div className="flex items-center justify-between rounded-lg border border-gold-300 p-4">
          <div className="flex items-center gap-3">
            <Icon icon="ph:user" className="h-5 w-5 text-black" />
            <div className="flex flex-col">
              <span className="font-medium text-black">{t.adults}</span>
              <span className="text-sm text-gold-700/80">{t.adultsAge}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              title={t.reduceAdults}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  passengers: Math.max(1, prev.passengers - 1),
                }))
              }
              className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-300 text-gold-700 transition-colors hover:bg-gold-100"
            >
              <Icon icon="ph:minus" className="h-4 w-4" />
            </button>
            <span className="w-5 text-center font-medium text-gold-800">
              {formData.passengers}
            </span>
            <button
              type="button"
              title={t.increaseAdults}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  passengers: Math.min(
                    selectedVehicle.maxPassengers,
                    prev.passengers + 1,
                  ),
                }))
              }
              className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-300 text-gold-700 transition-colors hover:bg-gold-100"
            >
              <Icon icon="ph:plus" className="h-4 w-4" />
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowAdvancedPassengerOptions((prev) => !prev)}
          className="inline-flex items-center gap-2 text-sm text-gold-700 transition hover:text-gold-800"
        >
          <Icon
            icon={
              showAdvancedPassengerOptions ? "ph:caret-up" : "ph:caret-down"
            }
            className="h-4 w-4"
          />
          {showAdvancedPassengerOptions ? t.hideAdvanced : t.showAdvanced}
        </button>

        {showAdvancedPassengerOptions && (
          <>
            <div className="flex items-center justify-between rounded-lg border border-gold-300 p-4">
              <div className="flex items-center gap-3">
                <Icon icon="ph:student" className="h-5 w-5 text-black" />
                <div className="flex flex-col">
                  <span className="font-medium text-black">{t.children}</span>
                  <span className="text-sm text-gold-700/80">
                    {t.childrenAge}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  title={t.reduceChildren}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      childSeats: Math.max(0, prev.childSeats - 1),
                    }))
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-300 text-gold-700 transition-colors hover:bg-gold-100"
                >
                  <Icon icon="ph:minus" className="h-4 w-4" />
                </button>
                <span className="w-5 text-center font-medium text-gold-800">
                  {formData.childSeats}
                </span>
                <button
                  type="button"
                  title={t.increaseChildren}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      childSeats: Math.min(3, prev.childSeats + 1),
                    }))
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-300 text-gold-700 transition-colors hover:bg-gold-100"
                >
                  <Icon icon="ph:plus" className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-gold-300 p-4">
              <div className="flex items-center gap-3">
                <Icon icon="ph:baby" className="h-5 w-5 text-black" />
                <div className="flex flex-col">
                  <span className="font-medium text-black">{t.babies}</span>
                  <span className="text-sm text-gold-700/80">
                    {t.babiesAge}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  title={t.reduceBabies}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      babySeats: Math.max(0, prev.babySeats - 1),
                    }))
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-300 text-gold-700 transition-colors hover:bg-gold-100"
                >
                  <Icon icon="ph:minus" className="h-4 w-4" />
                </button>
                <span className="w-5 text-center font-medium text-gold-800">
                  {formData.babySeats}
                </span>
                <button
                  type="button"
                  title={t.increaseBabies}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      babySeats: Math.min(3, prev.babySeats + 1),
                    }))
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-300 text-gold-700 transition-colors hover:bg-gold-100"
                >
                  <Icon icon="ph:plus" className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-gold-300 p-4">
              <div className="flex items-center gap-3">
                <Icon icon="ph:suitcase" className="h-5 w-5 text-black" />
                <div className="flex flex-col">
                  <span className="font-medium text-black">{t.luggage}</span>
                  <span className="text-sm text-gold-700/80">
                    {t.luggageDesc}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  title={t.reduceLuggage}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      luggage: Math.max(0, prev.luggage - 1),
                    }))
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-300 text-gold-700 transition-colors hover:bg-gold-100"
                >
                  <Icon icon="ph:minus" className="h-4 w-4" />
                </button>
                <span className="w-5 text-center font-medium text-gold-800">
                  {formData.luggage}
                </span>
                <button
                  type="button"
                  title={t.increaseLuggage}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      luggage: Math.min(
                        selectedVehicle.maxLuggage,
                        prev.luggage + 1,
                      ),
                    }))
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-300 text-gold-700 transition-colors hover:bg-gold-100"
                >
                  <Icon icon="ph:plus" className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
